import * as yup from 'yup';
import { Fields } from '~/features/checkout/models/fields';
import { useTranslation } from '~/shared/utils/translation';
import { passwordRegex } from '~/shared/utils';
import { isAddress1, isName, isZipCode, isPhoneWithoutCountryCode } from '~/utils/form-validators';
import { useBasket } from '~/features/basket/hooks';

/**
 * Yup documentaion https://github.com/jquense/yup
 * @returns validation schema
 */

export const useValidationSchema = (operableCountryCodes: string[]) => {
    const { translate } = useTranslation();
    const basket = useBasket();

    const validationShape = {
        [Fields.Email]: yup
            .string()
            .email(translate('message.emailValidationError'))
            .required('')
            .max(50, translate('message.inputValidationError.toLong', { length: '50' })),
        [Fields.ConfirmEmail]: yup
            .string()
            .oneOf(
                [yup.ref(Fields.Email), null],
                translate('message.emailValidationError.dontMatch'),
            )
            .required(translate('message.emailValidationError')),
        [Fields.Password]: yup.string().when(Fields.CreateAccount, {
            is: true,
            then: yup
                .string()
                .required('')
                .matches(passwordRegex, translate('message.passwordHelpText')),
        }),

        [Fields.ConfirmPassword]: yup.string().when(Fields.CreateAccount, {
            is: true,
            then: yup
                .string()
                .oneOf(
                    [yup.ref(Fields.Password), null],
                    translate('message.passwordValidationError.dontMatch'),
                )
                .required(''),
        }),

        [Fields.CreateAccountConsent]: yup.boolean().when(Fields.CreateAccount, {
            is: true,
            then: yup.boolean().oneOf([true], '').required(''),
        }),

        [Fields.FirstName]: yup
            .string()
            .trim()
            .required('')
            .matches(
                isName,
                translate('message.firstnameValidationError') ||
                    'Fornavn må kun indeholde bogstaver, mellemrum, komma, punktum og bindestreg',
            ),
        [Fields.LastName]: yup
            .string()
            .trim()
            .required('')
            .matches(
                isName,
                translate('message.lastnameValidationError') ||
                    'Efternavn må kun indeholde bogstaver, mellemrum, komma, punktum og bindestreg',
            ),

        [Fields.Address1]: yup
            .string()
            .required('')
            .matches(isAddress1, translate('message.addressMissingHouseNumber'))
            .max(50, translate('message.addressLengthValidationError')),

        [Fields.Address2]: yup.string().max(35, translate('message.addressLengthValidationError')),
        [Fields.PostalCode]: yup
            .string()
            .required('')
            .matches(isZipCode, translate('message.postNumberValidationError')),

        [Fields.City]: yup.string().required(''),

        [Fields.CountryCode]: yup.string().required(),

        [Fields.Phone]: yup.string().when(Fields.CountryCode, {
            // Opted for .indexOf() over .includes() for performance.
            is: (value: string) => operableCountryCodes.indexOf(value) >= 0,
            then: yup
                .string()
                .required('')
                .max(8, translate('message.phoneValidationError'))
                .matches(isPhoneWithoutCountryCode, translate('message.phoneValidationError')),
        }),

        [Fields.Consent]: yup.boolean().oneOf([true], '').required(''),

        [Fields.AdultConfirm]: basket?.c_adultPurchase
            ? yup.boolean().oneOf([true], '').required('')
            : yup.boolean(),
    };

    return yup.object().shape(validationShape);
};
